import { createSelector } from 'reselect'
import { checkoutAccommodationOfferView, getAccommodationItems } from 'checkout/selectors/view/accommodation'
import qs from 'qs'
import { hasFlightSessionExpired } from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import { getCharterFlightItems, getFlightItems, getNonCharterFlightItems } from './view/flights'
import { fareClassToUrlMap, parseFareClassFromUrl, toBaseFareClass } from 'constants/flight'
import type { FlightSortOptions } from 'constants/flight'
import { nonNullable } from 'lib/array/arrayUtils'
import type { FlightsFiltersValues } from 'lib/flights/flightFilterTypes'
import { FlightsFiltersPriceType } from 'lib/flights/flightFilterTypes'
import { getDefaultFlightSort } from 'lib/flights/flightUtils'
import { getTourFlightExpiry, getTourFlightSearchForPage, isTourFlightUpsellAvailable } from './tourFlightSelectors'

export const getCheckoutFlightFilters = createSelector(
  (state: App.State) => state.router.location.search,
  (state: App.State) => state.geo.currentRegionCode,
  getTourFlightSearchForPage,
  (search, regionCode, tourSearch): FlightsFiltersValues => {
    const params = new URLSearchParams(search)

    return {
      originAirportCode: tourSearch?.itinerary[0].departureAirport ?? (params.get('originAirportCode') || undefined),
      destinationAirportCode: tourSearch?.itinerary[0].arrivalAirport ?? (params.get('destinationAirportCode') || undefined),
      departDate: tourSearch?.itinerary[0].departureDate ?? (params.get('departDate') || undefined),
      returnDate: tourSearch ? undefined : params.get('returnDate') || undefined,
      fareClass: tourSearch?.fareClass ?? (() => {
        const parsed = parseFareClassFromUrl(params.get('fareClass') ?? undefined)
        return parsed ? toBaseFareClass(parsed) : undefined
      })(),
      stops: params.getAll('stops'),
      stopDurations: params.get('stopDurations') || undefined,
      airlines: params.getAll('airlines'),
      cabinTypes: params.getAll('cabinTypes'),
      durations: params.get('durations') || undefined,
      minDepartureTime: params.get('minDepartureTime') || undefined,
      maxDepartureTime: params.get('maxDepartureTime') || undefined,
      minOutboundArrivalTime: params.get('minOutboundArrivalTime') || undefined,
      maxOutboundArrivalTime: params.get('maxOutboundArrivalTime') || undefined,
      minReturnDepartureTime: params.get('minReturnDepartureTime') || undefined,
      maxReturnDepartureTime: params.get('maxReturnDepartureTime') || undefined,
      minArrivalTime: params.get('minArrivalTime') || undefined,
      maxArrivalTime: params.get('maxArrivalTime') || undefined,
      recommended: params.getAll('recommended'),
      sort: params.get('sort') as FlightSortOptions ?? getDefaultFlightSort(regionCode),
      bags: params.getAll('bags'),
      stopOverCities: params.getAll('stopOverCities'),
      alliances: params.getAll('alliances'),
      carriers: [],
      memberPrices: params.getAll('memberPrices'),
      priceFilterType: FlightsFiltersPriceType.ADULT,
    }
  },
)

export const getInflowFlightSearchParams = createSelector(
  (state: App.State) => getNonCharterFlightItems(state),
  (state: App.State) => getCharterFlightItems(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (flightItems, charterFlightItems, offerView): string | undefined => {
    const offerViews = offerView.data

    const pendingLeg = flightItems.find(item => item.charterBundleLeg && !item.flights.length)
    if (pendingLeg) {
      return qs.stringify({
        flightItemId: pendingLeg.itemId,
        departDate: pendingLeg.charterBundleLeg!.departureDate,
        originAirportCode: pendingLeg.originAirportCode,
        destinationAirportCode: pendingLeg.destinationAirportCode,
        fareClass: pendingLeg.selectedCabinClass ? fareClassToUrlMap[pendingLeg.selectedCabinClass] : undefined,
      })
    }
    if (charterFlightItems[0] && flightItems[0]) {
      return qs.stringify({
        departDate: charterFlightItems[0].flights.at(0)?.departingDate,
        returnDate: charterFlightItems[0].flights.at(-1)?.departingDate,
        originAirportCode: flightItems[0].originAirportCode,
        destinationAirportCode: flightItems[0].destinationAirportCode,
        fareClass: charterFlightItems[0].flights.at(0)?.fareFamily?.cabin?.toLowerCase(),
      })
    }
    if (offerViews[0] && flightItems[0]) {
      return qs.stringify({
        departDate: offerViews[0].startDate,
        returnDate: offerViews[0].endDate,
        originAirportCode: flightItems[0].originAirportCode,
        destinationAirportCode: flightItems[0].destinationAirportCode,
        fareClass: flightItems[0].selectedCabinClass ? fareClassToUrlMap[flightItems[0].selectedCabinClass] : undefined,
      })
    }
  },
)

export const isFlightSessionExpired = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => isTourFlightUpsellAvailable(state),
  (state: App.State) => getTourFlightExpiry(state),
  (items, isTourFlightUpsell, tourFlightExpiry) => {
    return items.length > 0 && (isTourFlightUpsell
      ? hasFlightSessionExpired(tourFlightExpiry)
      : hasFlightSessionExpired())
  },
)

export const isStandaloneFlight = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getNonCharterFlightItems(state),
  (cartItems, flightItems): boolean => flightItems.length === 1 && cartItems.length === 1,
)

export const getCheckoutFlightOccupancies = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => getNonCharterFlightItems(state),
  (accommodationItems, flightItems): App.Occupants => {
    // If we have flight items, check if the first flight is bundled or standalone
    if (flightItems.length > 0) {
      const flightItem = flightItems[0]
      const isFlightBundled = (flightItem.bundledItemIds?.length ?? 0) > 0

      // For standalone flights (no bundledItemIds), use flight item occupancy directly
      if (!isFlightBundled) {
        return flightItem.occupants
      }
    }

    // For bundled flights or when no flight items exist, sum up accommodation occupancies
    const occupants = nonNullable(accommodationItems.map(item => (item as any).occupancy))
    return countOccupantsForFlights(occupants)
  },
)
